'use client';

import React, { useEffect } from 'react';
import { ToolItem, CategoryItem } from '@/config/types';
import { usePersonalTools } from '@/hooks/usePersonalTools';
import { useIsEmbed } from '@/hooks/useIsEmbed';
import { isWebMcpSupported, selectWebMcpCapabilities } from '@/lib/capabilities/webmcp/bridge';

interface ToolPageClientViewProps {
  tool: ToolItem;
  category: CategoryItem;
  initialIsEmbed?: boolean;
  smartInputs?: Record<string, string | number>;
  children: React.ReactNode;
}

export function ToolPageClientView({
  tool,
  category,
  initialIsEmbed = false,
  smartInputs = {},
  children,
}: ToolPageClientViewProps) {
  const { recordVisit } = usePersonalTools();
  const isClientEmbed = useIsEmbed();

  useEffect(() => {
    // Automatically record visit in recent tools history
    recordVisit(tool.slug);
  }, [tool.slug, recordVisit]);

  useEffect(() => {
    // Synchronize embed class if client-side navigation enters embed mode
    if (initialIsEmbed || isClientEmbed) {
      document.documentElement.classList.add('bop-embed-mode');
    }
  }, [initialIsEmbed, isClientEmbed]);

  useEffect(() => {
    // WebMCP Progressive Enhancement: Unsupported browser => zero-cost no-op
    if (!isWebMcpSupported()) return;

    let unregisterFn: (() => void) | undefined;
    (async () => {
      try {
        const [
          { capabilityRegistry },
          { ensureAllCapabilitiesRegistered },
          { registerCapabilityWebMcp },
        ] = await Promise.all([
          import('@/lib/capabilities/registry/registry'),
          import('@/lib/capabilities/registry/auto-register'),
          import('@/lib/capabilities/webmcp/bridge'),
        ]);

        ensureAllCapabilitiesRegistered();
        const capabilities = capabilityRegistry.getByToolSlug(tool.slug);
        const handles = selectWebMcpCapabilities(capabilities).map((contract) => registerCapabilityWebMcp(contract));
        unregisterFn = () => handles.forEach((handle) => handle.unregister());
      } catch (err) {
        console.warn('[WebMCP] Progressive registration skipped:', err);
      }
    })();

    return () => {
      if (unregisterFn) {
        unregisterFn();
      }
    };
  }, [tool.slug]);

  // Embed contract parity guarantee: initialInputs={smartInputs}
  // Standard full render so hooks match identically between SSR and client hydration
  return <>{children}</>;
}
